import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AccountsAdminApi } from '../adminApi.js'
import type { AdminLoginResult } from '../index.js'
import type { AdminUser, AdminUserWithPassword, Invitation, InvitationResult, UpdateAdminInput } from '../index.js'
import { SecretBanner, EndpointHint, Skeleton } from '../../core/index.js'
import { AdminIcon, RootAdminIcon, SalesIcon } from '../../core/index.js'
import { formatDateTime } from '../../core/index.js'
import { MailPlus, RefreshCw, Save, Send, Trash2, XCircle } from 'lucide-react'
import { Button } from '../../core/index.js'
import { LoadFailure, RefreshButton } from '../../core/index.js'
import { AdminEmailForm } from './AdminEmailForm.js'

/** The admin calls this screen uses. */
type AdminApi = AccountsAdminApi

const QUERY_KEY = ['admin', 'admins'] as const
const INVITATIONS_KEY = ['admin', 'admin-invitations'] as const

/** "emailed 21 Oct, 9:07 AM", "the link was shown to you", "the email failed". */
export function describeSends(invitation: Invitation): string {
  const last = invitation.sends[invitation.sends.length - 1]
  if (!last) {
    return 'not sent'
  }
  if (last.status === 'shown') {
    return `link shown ${formatDateTime(last.at)}`
  }
  const verb = last.channel === 'sms' ? 'texted' : 'emailed'
  return last.status === 'sent' ? `${verb} ${formatDateTime(last.at)}` : `the ${last.channel === 'sms' ? 'text' : 'email'} failed${last.reason ? `: ${last.reason}` : ''}`
}
const PASSWORD_LABEL = "One-time password — copy it now, it won't be shown again"

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

function adminName(admin: AdminUser): string {
  const name = `${admin.firstName} ${admin.lastName}`.trim()
  return name === '' ? admin.username : name
}

/** The invite form (invitations plan phase 1): the login is made at once, switched off, and the person is emailed a link and a code to set their own password with. */
function InviteAdminForm({ onCreate, isPending, error }: { onCreate: (input: { username: string; firstName: string; lastName: string; role: 'admin' | 'sales' }) => void; isPending: boolean; error: string | null }) {
  const [username, setUsername] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState<'admin' | 'sales'>('admin')
  const canSubmit = username.trim() !== '' && firstName.trim() !== '' && lastName.trim() !== '' && !isPending

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (!canSubmit) {
      return
    }
    onCreate({ username: username.trim(), firstName: firstName.trim(), lastName: lastName.trim(), role })
    setUsername('')
    setFirstName('')
    setLastName('')
  }

  const field = 'w-full border border-marine/30 rounded px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-coral'
  return (
    <form onSubmit={handleSubmit} className="bg-white border border-marine/15 rounded p-4">
      <div className="text-xs font-extrabold text-ink mb-1">
        Invite a login <EndpointHint>POST /admin/admins/invitations</EndpointHint>
      </div>
      <p className="text-[11px] text-ink-mid mb-3">
        The username is their email address. They are emailed a link and a six-digit code, good for seven days, and set their own password; nobody sees it. Until they
        accept, the login is listed as invited and cannot sign in. Where this deployment cannot email, the link and code are shown here once for you to pass on.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Email (username)</span>
          <input type="email" value={username} onChange={(event) => setUsername(event.target.value)} className={field} autoComplete="off" required />
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">First name</span>
          <input value={firstName} onChange={(event) => setFirstName(event.target.value)} className={field} required />
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Last name</span>
          <input value={lastName} onChange={(event) => setLastName(event.target.value)} className={field} required />
        </label>
      </div>
      <fieldset className="mt-3">
        <legend className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Role</legend>
        <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-6">
          <label className="flex items-start gap-2 text-xs text-ink cursor-pointer">
            <input type="radio" name="new-login-role" className="mt-0.5" checked={role === 'admin'} onChange={() => setRole('admin')} />
            <span>
              <span className="font-bold">Admin</span>
              <span className="block text-[11px] text-ink-mid">Every client and every admin page, except managing logins.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-xs text-ink cursor-pointer">
            <input type="radio" name="new-login-role" className="mt-0.5" checked={role === 'sales'} onChange={() => setRole('sales')} />
            <span>
              <span className="font-bold">Sales</span>
              <span className="block text-[11px] text-ink-mid">Creates demo clients and sees only the ones they created. No real clients, no admin pages. The role cannot be changed later.</span>
            </span>
          </label>
        </div>
      </fieldset>
      {error && <p className="mt-2 text-[11px] font-bold text-coral">{error}</p>}
      <Button type="submit" icon={MailPlus} disabled={!canSubmit} className="mt-3">
        {isPending ? 'Inviting…' : role === 'sales' ? 'Invite sales user' : 'Invite admin'}
      </Button>
    </form>
  )
}

/** The inline name editor for one row: both parts required, saved with PATCH /admin/admins/{username}. */
function EditNameForm({ admin, onSave, onCancel, isPending }: { admin: AdminUser; onSave: (input: { firstName: string; lastName: string }) => void; onCancel: () => void; isPending: boolean }) {
  const [firstName, setFirstName] = useState(admin.firstName)
  const [lastName, setLastName] = useState(admin.lastName)
  const canSave = firstName.trim() !== '' && lastName.trim() !== '' && !isPending
  const field = 'w-28 rounded border border-marine/30 bg-white px-2 py-1 text-[11px] text-ink'

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (canSave) {
      onSave({ firstName: firstName.trim(), lastName: lastName.trim() })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-1 flex flex-wrap items-center gap-1.5">
      <input value={firstName} onChange={(event) => setFirstName(event.target.value)} placeholder="First name" aria-label="First name" className={field} autoFocus required />
      <input value={lastName} onChange={(event) => setLastName(event.target.value)} placeholder="Last name" aria-label="Last name" className={field} required />
      <Button type="submit" icon={Save} size="sm" disabled={!canSave}>
        {isPending ? 'Saving…' : 'Save'}
      </Button>
      <button type="button" onClick={onCancel} disabled={isPending} className="text-[11px] font-bold text-ink-mid hover:text-ink disabled:opacity-40">
        Cancel
      </button>
    </form>
  )
}

interface AdminRowProps {
  admin: AdminUser
  isSelf: boolean
  onRotate: () => void
  onRemove: () => void
  onRename: (input: { firstName: string; lastName: string }) => void
  onEmailChange: (email: string | null) => void
  isRenaming: boolean
  isBusy: boolean
}

function AdminRow({ admin, isSelf, onRotate, onRemove, onRename, onEmailChange, isRenaming, isBusy }: AdminRowProps) {
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [editingEmail, setEditingEmail] = useState(false)
  const isRoot = admin.role === 'root'
  const isSales = admin.role === 'sales'
  const isPending = admin.invitationStatus === 'pending'
  const hasName = `${admin.firstName} ${admin.lastName}`.trim() !== ''
  return (
    <tr className="border-t border-marine/10 text-ink align-top">
      <td className="py-2 pr-3">
        <div className="flex items-center gap-1.5 font-bold">
          {/* The king for the root, the queen for every other admin: the rank reads before the name. The Role column names it for screen readers. */}
          {isRoot ? <RootAdminIcon className="h-3.5 w-3.5 text-coral" /> : isSales ? <SalesIcon className="h-3.5 w-3.5 text-marine" /> : <AdminIcon className="h-3.5 w-3.5 text-marine" />}
          {adminName(admin)}
          {isSelf && <span className="text-[10px] font-bold text-ink-mid">(you)</span>}
          {!editingName && (
            <button type="button" onClick={() => setEditingName(true)} disabled={isBusy} className="text-[10px] font-bold text-marine hover:text-marine-dk disabled:opacity-40">
              {hasName ? 'Edit name' : 'Add name'}
            </button>
          )}
        </div>
        <div className="font-mono text-[11px] text-ink-mid">{admin.username}</div>
        <div className="flex items-center gap-1.5 text-[11px] text-ink-mid">
          {admin.email ? <span>Email: {admin.email}</span> : <span className="italic">No contact email</span>}
          {!editingEmail && (
            <button type="button" onClick={() => setEditingEmail(true)} disabled={isBusy} className="text-[10px] font-bold text-marine hover:text-marine-dk disabled:opacity-40">
              {admin.email ? 'Edit email' : 'Add email'}
            </button>
          )}
        </div>
        {isRoot && <div className="text-[10px] text-ink-mid">New feedback is emailed here{admin.email ? '' : ' (or to the username, when it is an email address)'}.</div>}
        {editingEmail && (
          <AdminEmailForm
            admin={admin}
            isPending={isRenaming}
            onCancel={() => setEditingEmail(false)}
            onSave={(email) => {
              onEmailChange(email)
              setEditingEmail(false)
            }}
          />
        )}
        {editingName && (
          <EditNameForm
            admin={admin}
            isPending={isRenaming}
            onCancel={() => setEditingName(false)}
            onSave={(input) => {
              onRename(input)
              setEditingName(false)
            }}
          />
        )}
      </td>
      <td className="py-2 pr-3">
        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold border rounded ${isRoot ? 'bg-coral-lt text-coral border-coral/40' : 'bg-marine-lt text-marine-dk border-marine/30'}`}>
          {isRoot ? 'Root' : isSales ? 'Sales' : 'Admin'}
        </span>
      </td>
      <td className="py-2 pr-3 text-ink-mid whitespace-nowrap">{isPending ? <span className="font-bold text-gold">Invited · waiting</span> : admin.lastLoginAt ? formatDateTime(admin.lastLoginAt) : 'Never'}</td>
      <td className="py-2 pr-3 text-ink-mid whitespace-nowrap">
        {formatDateTime(admin.createdAt)}
        {!isRoot && <div className="text-[10px]">by {admin.createdBy}</div>}
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        {isRoot ? (
          <span className="inline-flex items-center gap-2">
            <span className="text-[10px] text-ink-mid">Cannot be removed</span>
            <Button icon={RefreshCw} variant="secondary" size="sm" onClick={onRotate} disabled={isBusy}>
              Rotate password
            </Button>
          </span>
        ) : confirmRemove ? (
          <span className="inline-flex items-center gap-2">
            <span className="text-[11px] text-ink">Remove {admin.username}?</span>
            <Button icon={Trash2} variant="secondary" size="sm" onClick={onRemove} disabled={isBusy}>
              Remove
            </Button>
            <button type="button" onClick={() => setConfirmRemove(false)} className="text-[11px] font-bold text-ink-mid hover:text-ink">
              Cancel
            </button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-2">
            {/* A pending login has no password to rotate: its invitation is resent or withdrawn below. */}
            {!isPending && (
              <Button icon={RefreshCw} variant="secondary" size="sm" onClick={onRotate} disabled={isBusy}>
                Rotate password
              </Button>
            )}
            <Button icon={Trash2} variant="secondary" size="sm" onClick={() => setConfirmRemove(true)} disabled={isBusy}>
              Remove
            </Button>
          </span>
        )}
      </td>
    </tr>
  )
}

/**
 * The root admin's page, and nobody else's: who can sign in to this admin
 * area. The root is the credential the API is configured with, so it is
 * listed and can be named and have its password rotated here but not
 * removed; every other admin is created here, with a generated password
 * shown once, and can be renamed afterwards.
 */
interface AdminsPageProps {
  api: AdminApi
  session: AdminLoginResult
  /** Told when a password was rotated, so the app can switch to it when it is the signed-in admin's own. */
  onPasswordRotated: (username: string, password: string) => void
  /** Told when an admin's name was set, so the header can follow when it is the signed-in admin's own. */
  onNameChanged: (admin: AdminUser) => void
}

export function AdminsPage({ api, session, onPasswordRotated, onNameChanged }: AdminsPageProps) {
  const queryClient = useQueryClient()
  const adminsQuery = useQuery({ queryKey: QUERY_KEY, queryFn: api.listAdmins })
  const invitationsQuery = useQuery({ queryKey: INVITATIONS_KEY, queryFn: api.listAdminInvitations })
  const [secret, setSecret] = useState<AdminUserWithPassword | null>(null)
  const [handOff, setHandOff] = useState<InvitationResult | null>(null)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: INVITATIONS_KEY })
  }
  const invited = (result: InvitationResult) => {
    setHandOff(result.handOff ? result : null)
    refresh()
  }
  const create = useMutation({ mutationFn: api.inviteAdmin, onSuccess: invited })
  const resend = useMutation({ mutationFn: api.resendAdminInvitation, onSuccess: invited })
  const revoke = useMutation({ mutationFn: api.revokeAdminInvitation, onSuccess: refresh })
  const rotate = useMutation({
    mutationFn: api.rotateAdminPassword,
    onSuccess: (rotated) => {
      setSecret(rotated)
      onPasswordRotated(rotated.admin.username, rotated.password)
      refresh()
    },
  })
  const remove = useMutation({ mutationFn: api.removeAdmin, onSuccess: refresh })
  const rename = useMutation({
    mutationFn: ({ username, ...input }: { username: string } & UpdateAdminInput) => api.updateAdmin(username, input),
    onSuccess: (admin) => {
      onNameChanged(admin)
      refresh()
    },
  })
  const isBusy = rotate.isPending || remove.isPending || rename.isPending || resend.isPending || revoke.isPending
  const pendingInvitations = (invitationsQuery.data ?? []).filter((invitation) => invitation.status === 'pending')

  return (
    <div className="px-6 py-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-5">
        <p className="max-w-2xl text-xs leading-relaxed text-ink-mid">
          Only the root admin sees this page. Admins you invite here sign in at the same screen you do and can do everything you can across every client,
          except manage admins. A rotated password is shown once for you to pass on. Rotating your own password replaces the one the API was deployed with;
          this session carries on with the new one, and it is what you sign in with from now on.
        </p>

        {secret && <SecretBanner label={`${secret.admin.username} — ${PASSWORD_LABEL}`} value={secret.password} onDismiss={() => setSecret(null)} />}
        {handOff?.handOff && (
          <SecretBanner
            label={`${handOff.invitation.email} — this deployment cannot email, so pass the link on yourself (or the code ${handOff.handOff.code}, entered with the email). It expires in seven days.`}
            value={handOff.handOff.link}
            onDismiss={() => setHandOff(null)}
          />
        )}

        <InviteAdminForm onCreate={(input) => create.mutate(input)} isPending={create.isPending} error={create.isError ? errorMessage(create.error, 'Could not invite the admin.') : null} />

        {pendingInvitations.length > 0 && (
          <section className="bg-white border border-marine/15 rounded p-4">
            <div className="text-xs font-extrabold text-ink mb-2">
              Invited, waiting <EndpointHint>GET /admin/admins/invitations</EndpointHint>
            </div>
            {resend.isError && <p className="mb-2 text-[11px] font-bold text-coral">{errorMessage(resend.error, 'Could not resend the invitation.')}</p>}
            {revoke.isError && <p className="mb-2 text-[11px] font-bold text-coral">{errorMessage(revoke.error, 'Could not withdraw the invitation.')}</p>}
            <ul className="divide-y divide-marine/10 text-[11px]">
              {pendingInvitations.map((invitation) => (
                <li key={invitation.invitationId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span>
                    <span className="font-bold text-ink">
                      {invitation.firstName} {invitation.lastName}
                    </span>
                    <span className="text-ink-mid"> · {invitation.email} · {invitation.role === 'sales' ? 'sales' : 'admin'} · {describeSends(invitation)} · expires {formatDateTime(invitation.expiresAt)}</span>
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Button icon={Send} variant="secondary" size="sm" onClick={() => resend.mutate(invitation.invitationId)} disabled={isBusy}>
                      Resend
                    </Button>
                    <Button icon={XCircle} variant="secondary" size="sm" onClick={() => revoke.mutate(invitation.invitationId)} disabled={isBusy}>
                      Withdraw
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="bg-white border border-marine/15 rounded p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-xs font-extrabold text-ink">
              Admins <EndpointHint>GET /admin/admins</EndpointHint>
            </div>
            <RefreshButton
              size="sm"
              onRefresh={() => Promise.all([adminsQuery.refetch(), invitationsQuery.refetch()])}
              refreshing={adminsQuery.isFetching || invitationsQuery.isFetching}
            />
          </div>
          {rotate.isError && <p className="mb-2 text-[11px] font-bold text-coral">{errorMessage(rotate.error, 'Could not rotate the password.')}</p>}
          {remove.isError && <p className="mb-2 text-[11px] font-bold text-coral">{errorMessage(remove.error, 'Could not remove the admin.')}</p>}
          {rename.isError && <p className="mb-2 text-[11px] font-bold text-coral">{errorMessage(rename.error, 'Could not save the change.')}</p>}
          {adminsQuery.isPending ? (
            <Skeleton label="Loading admins" />
          ) : adminsQuery.isError ? (
            <LoadFailure size="xs" message={errorMessage(adminsQuery.error, 'Could not load the admins.')} onRetry={() => void adminsQuery.refetch()} retrying={adminsQuery.isFetching} />
          ) : (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
                  <th className="py-1 pr-3 font-bold">Admin</th>
                  <th className="py-1 pr-3 font-bold">Role</th>
                  <th className="py-1 pr-3 font-bold">Last sign-in</th>
                  <th className="py-1 pr-3 font-bold">Created</th>
                  <th className="py-1 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminsQuery.data.map((admin) => (
                  <AdminRow
                    key={admin.username}
                    admin={admin}
                    isSelf={admin.username === session.username}
                    isBusy={isBusy}
                    onRotate={() => rotate.mutate(admin.username)}
                    onRemove={() => remove.mutate(admin.username)}
                    onRename={(input) => rename.mutate({ username: admin.username, ...input })}
                    onEmailChange={(email) => rename.mutate({ username: admin.username, email })}
                    isRenaming={rename.isPending && rename.variables?.username === admin.username}
                  />
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  )
}
